// Employee payment-methods React Query hooks. Follows the My Pay self-service
// pattern: it reuses the `employeeApi` axios instance in hrApi (real camelCase
// `accessToken` + silent refresh) — NOT the shared apiClient, which reads a
// snake_case token and 401→logout. Server state lives in the shared queryClient.
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import {
  getMyPaymentMethods,
  addMyPaymentMethod,
  updateMyPaymentMethod,
  deactivateMyPaymentMethod,
  setPrimaryPaymentMethod,
  setMyDefaultPaymentMethod,
  setEmployeeDefaultPaymentMethod,
  getEmployeePaymentMethods,
  getPaymentMethodAudit,
  getPendingPaymentMethods,
  approvePaymentMethod,
  rejectPaymentMethod,
  type PaymentMethod,
  type PaymentMethodCreate,
} from '@/lib/hrApi';

const KEYS = {
  mine: ['payment-methods', 'me'] as const,
  pending: ['payment-methods', 'pending'] as const,
  employee: (employeeId: string) => ['payment-methods', 'employee', employeeId] as const,
  audit: (employeeId: string, id: string) =>
    ['payment-methods', 'audit', employeeId, id] as const,
};

// After any decision or self-service change, refresh the lists so status,
// primary flag and the pending-approvals worklist all stay in sync.
function invalidateAll() {
  void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
}

// ---- self-service (logged-in employee) ----
export function useMyPaymentMethods() {
  return useQuery({
    queryKey: KEYS.mine,
    queryFn: () => getMyPaymentMethods(),
  });
}

export function useAddPaymentMethod() {
  return useMutation({
    mutationFn: (data: PaymentMethodCreate) => addMyPaymentMethod(data),
    onSuccess: invalidateAll,
  });
}

export function useUpdatePaymentMethod() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PaymentMethodCreate> }) =>
      updateMyPaymentMethod(id, data),
    onSuccess: invalidateAll,
  });
}

export function useDeactivatePaymentMethod() {
  return useMutation({
    mutationFn: (id: string) => deactivateMyPaymentMethod(id),
    onSuccess: invalidateAll,
  });
}

export function useSetPrimary() {
  return useMutation({
    mutationFn: (id: string) => setPrimaryPaymentMethod(id),
    onSuccess: invalidateAll,
  });
}

// Make one of MY approved methods the destination payroll pays.
export function useSetMyDefault() {
  return useMutation({
    mutationFn: (id: string) => setMyDefaultPaymentMethod(id),
    onSuccess: invalidateAll,
  });
}

// ---- admin / HR-Finance ----

export function useSetEmployeeDefault(employeeId: string) {
  return useMutation({
    mutationFn: (id: string) => setEmployeeDefaultPaymentMethod(employeeId, id),
    onSuccess: invalidateAll,
  });
}
export function usePendingPaymentMethods() {
  return useQuery({
    queryKey: KEYS.pending,
    queryFn: () => getPendingPaymentMethods(),
  });
}

export function useEmployeePaymentMethods(employeeId?: string | null) {
  return useQuery({
    queryKey: KEYS.employee(employeeId || ''),
    queryFn: () => getEmployeePaymentMethods(employeeId as string),
    enabled: !!employeeId,
  });
}

export function usePaymentMethodAudit(employeeId?: string | null, id?: string | null) {
  return useQuery({
    queryKey: KEYS.audit(employeeId || '', id || ''),
    queryFn: () => getPaymentMethodAudit(employeeId as string, id as string),
    enabled: !!employeeId && !!id,
  });
}

export function useApprovePaymentMethod() {
  return useMutation({
    mutationFn: (id: string) => approvePaymentMethod(id),
    onSuccess: invalidateAll,
  });
}

export function useRejectPaymentMethod() {
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectPaymentMethod(id, reason),
    onSuccess: invalidateAll,
  });
}

export type { PaymentMethod, PaymentMethodCreate };
